import { useTranslation } from 'react-i18next';
import createGridCustomCells from '@components-clean/core/grid/custom-cell';
import { YesNoCell } from '@components-clean/core/grid-fe/custom-cells';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import { FieldDefinitionDto, RowDataSourceDto } from '@interfaces-clean/field-rdl-definition.interface';
import {
    commonCreatedUpdatedColumns,
    commonHiddenColumns,
    commonVisibleColumns,
} from '@pages-clean/admin/study/manage-panels/columns/column-list';
import { PanelGrid } from '@pages-clean/admin/study/manage-panels/columns/panel-grid-interface';
import { OptionsEventRatesOT } from '@pages-clean/admin/study/manage-panels/tab-content/panel-details/types/eventRatesOT/interfaces';
import {
    AutomationRange,
    ControlHubModelPeriodType,
    EventRatesOTBenchmarkType,
    EventRatesOTCountryBenchmarkType,
    EventRatesOTThresholdType,
    ModelPlotBy,
    ModelPlotCalculation,
    ModelScaleBy,
    PanelType,
} from 'types/graphql-global-types';
import { getPanels_OpraQueries_panels_Panel } from '@queries/generated/central-monitoring.generated';

class EventRatesOTGrid implements PanelGrid {
    // TODO: investigate not using the hook here
    // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
    // eslint-disable-next-line @eslint-react/rules-of-hooks
    private t = useTranslation(['admin'], { keyPrefix: 'managePanels' }).t;

    // TODO: investigate not using the hook here
    // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
    // eslint-disable-next-line @eslint-react/rules-of-hooks
    private tOptions = useTranslation('admin', {
        keyPrefix: 'managePanels.panelDetails.eventRatesOT.options',
    }).t;

    private cm2Fields: FieldDefinitionDto[] | null | undefined;

    private cm2DataSources: RowDataSourceDto[] | null | undefined;

    private ScaleByOptions: { id: ModelScaleBy; name: string }[];

    private PlotByOptions: { id: ModelPlotBy; name: string }[];

    private ThresholdTypeOptions: { id: EventRatesOTThresholdType; name: string }[];

    private BenchmarkTypeOptions: { id: EventRatesOTBenchmarkType; name: string }[];

    private CountryBenchmarkTypeOptions: { id: EventRatesOTCountryBenchmarkType; name: string }[];

    private PlotCalculationOptions: { id: ModelPlotCalculation; name: string }[];

    private RangeOptions: { id: AutomationRange; name: string }[];

    private MinTimeOnStudyPeriodOptions: { id: ControlHubModelPeriodType; name: string }[];

    private TooltipCell = createGridCustomCells().TooltipCell;

    private ScaleTimePointOptions: { id: ModelScaleBy; name: string }[];

    constructor(
        cm2Fields: FieldDefinitionDto[] | null | undefined,
        cm2DataSources: RowDataSourceDto[] | null | undefined,
    ) {
        this.cm2Fields = cm2Fields;
        this.cm2DataSources = cm2DataSources;

        ({
            ScaleByOptions: this.ScaleByOptions,
            ScaleTimePointOptions: this.ScaleTimePointOptions,
            PlotByOptions: this.PlotByOptions,
            ThresholdTypeOptions: this.ThresholdTypeOptions,
            BenchmarkTypeOptions: this.BenchmarkTypeOptions,
            CountryBenchmarkTypeOptions: this.CountryBenchmarkTypeOptions,
            PlotCalculationOptions: this.PlotCalculationOptions,
            MinTimeOnStudyPeriodOptions: this.MinTimeOnStudyPeriodOptions,
            RangeOptions: this.RangeOptions,
        } = OptionsEventRatesOT(this.tOptions));
    }

    GridTitle(): string {
        return this.t('grid.eventRatesOT.title');
    }

    // eslint-disable-next-line class-methods-use-this
    ExportName(): string {
        return 'Event Rates Over Time';
    }

    PanelData(panels: (getPanels_OpraQueries_panels_Panel | null)[] | null | undefined): never[] | undefined {
        const filteredPanels = panels?.filter((panel) => panel?.panelType === PanelType.EVENT_RATES_OVER_TIME);

        // @ts-ignore
        return filteredPanels
            ?.map((panel) => ({
                ...panel,
                updatedByFullName: `${panel?.updatedBy?.firstName} ${panel?.updatedBy?.lastName}`,
                createdByFullName: `${panel?.createdBy?.firstName} ${panel?.createdBy?.lastName}`,
                active: panel?.active || false,
                name: panel?.name || '',
                rowData: this.cm2DataSources?.find(
                    (field) => field?.id === panel?.eventRatesOverTimeParameters?.rowData,
                )?.name,
                timepointsList: this.cm2DataSources?.find(
                    (field) => field?.id === panel?.eventRatesOverTimeParameters?.timepointsList,
                )?.name,
                rowDataDate: this.cm2Fields?.find(
                    (field) => field?.key === panel?.eventRatesOverTimeParameters?.rowDataDate,
                )?.name,
                calcStartDate: this.cm2Fields?.find(
                    (field) => field?.key === panel?.eventRatesOverTimeParameters?.calcStartDate,
                )?.name,
                calcEndDates: panel?.eventRatesOverTimeParameters?.calcEndDates
                    ?.map((fieldKey) => this.cm2Fields?.find((field) => field?.key === fieldKey)?.name)
                    .join(', '),
                timepointDate: this.cm2Fields?.find(
                    (field) => field?.key === panel?.eventRatesOverTimeParameters?.timepointDate,
                )?.name,
                qtl: panel?.eventRatesOverTimeParameters?.qtl || false,
                reportOutOfTolerance: panel?.eventRatesOverTimeParameters?.reportOutOfTolerance || false,
                scaleBy:
                    panel?.eventRatesOverTimeParameters?.scaleBy === ModelScaleBy.PER_SUBJECT_WEEK ||
                    panel?.eventRatesOverTimeParameters?.scaleBy === ModelScaleBy.PER_SUBJECT_MONTH ||
                    panel?.eventRatesOverTimeParameters?.scaleBy === ModelScaleBy.PER_SUBJECT_YEAR ||
                    panel?.eventRatesOverTimeParameters?.scaleBy === ModelScaleBy.PER_10_SUBJECT_YEARS ||
                    panel?.eventRatesOverTimeParameters?.scaleBy === ModelScaleBy.PER_100_SUBJECT_YEARS ||
                    panel?.eventRatesOverTimeParameters?.scaleBy === ModelScaleBy.PER_1000_SUBJECT_YEARS
                        ? this.ScaleByOptions.find((sb) => sb.id === panel?.eventRatesOverTimeParameters?.scaleBy)?.name
                        : undefined,
                scaleTimepoint:
                    panel?.eventRatesOverTimeParameters?.scaleBy === ModelScaleBy.PER_SUBJECT_TIME_POINT ||
                    panel?.eventRatesOverTimeParameters?.scaleBy === ModelScaleBy.PER_10_SUBJECT_TIME_POINTS ||
                    panel?.eventRatesOverTimeParameters?.scaleBy === ModelScaleBy.PER_100_SUBJECT_TIME_POINTS ||
                    panel?.eventRatesOverTimeParameters?.scaleBy === ModelScaleBy.PER_1000_SUBJECT_TIME_POINTS
                        ? this.ScaleTimePointOptions.find(
                              (sb) => sb.id === panel?.eventRatesOverTimeParameters?.scaleBy,
                          )?.name
                        : undefined,
                plotBy: this.PlotByOptions.find((pb) => pb.id === panel?.eventRatesOverTimeParameters?.plotBy)?.name,
                thresholdType: this.ThresholdTypeOptions.find(
                    (tt) => tt.id === panel?.eventRatesOverTimeParameters?.thresholdType,
                )?.name,
                benchmarkType: this.BenchmarkTypeOptions.find(
                    (bt) => bt.id === panel?.eventRatesOverTimeParameters?.benchmarkType,
                )?.name,
                countryBenchmark: this.CountryBenchmarkTypeOptions.find(
                    (cb) => cb.id === panel?.eventRatesOverTimeParameters?.countryBenchmark,
                )?.name,
                cumulativeOrTimePeriod: this.PlotCalculationOptions.find(
                    (pc) => pc.id === panel?.eventRatesOverTimeParameters?.cumulativeOrTimePeriod,
                )?.name,
                minTimeOnStudyPeriod: this.MinTimeOnStudyPeriodOptions.find(
                    (pc) => pc.id === panel?.eventRatesOverTimeParameters?.minTimeOnStudyPeriod,
                )?.name,
                minTimeOnStudyUnit: panel?.eventRatesOverTimeParameters?.minTimeOnStudyUnit,
                numberOfTimePeriods: panel?.eventRatesOverTimeParameters?.numberOfTimePeriods,
                automationRange: this.RangeOptions.find(
                    (pc) => pc.id === panel?.eventRatesOverTimeParameters?.automationRange,
                )?.name,
                rateBy:
                    panel?.eventRatesOverTimeParameters?.scaleBy === ModelScaleBy.PER_SUBJECT_TIME_POINT ||
                    panel?.eventRatesOverTimeParameters?.scaleBy === ModelScaleBy.PER_10_SUBJECT_TIME_POINTS ||
                    panel?.eventRatesOverTimeParameters?.scaleBy === ModelScaleBy.PER_100_SUBJECT_TIME_POINTS ||
                    panel?.eventRatesOverTimeParameters?.scaleBy === ModelScaleBy.PER_1000_SUBJECT_TIME_POINTS
                        ? this.tOptions('TIMEPOINTS')
                        : this.tOptions('PERIOD'),
            }))
            .sort((a, b) => Number(b.active) - Number(a.active) || a.name.localeCompare(b.name));
    }

    Columns(): GridColumnProps[] {
        return [
            ...commonVisibleColumns(this.t, 'eventRatesOverTimeParameters'),
            {
                field: 'qtl',
                title: this.tOptions('panel-qtl'),
                id: 'panel-qtl',
                cells: { data: YesNoCell },
                filter: 'boolean',
                minWidth: 95,
            },
            {
                field: 'reportOutOfTolerance',
                title: this.tOptions('panel-reportoot'),
                id: 'panel-reportoot',
                cells: { data: YesNoCell },
                filter: 'boolean',
                minWidth: 95,
            },
            {
                field: 'rowData',
                title: this.tOptions('panel-RowData'),
                id: 'panel-RowData',
                filter: 'text',
                minWidth: 210,
                cells: { data: this.TooltipCell({ isCsv: false }) },
            },
            {
                field: 'rowDataDate',
                title: this.tOptions('panel-RowDataDateField'),
                id: 'panel-RowDataDateField',
                filter: 'text',
                minWidth: 210,
                cells: { data: this.TooltipCell({ isCsv: false }) },
            },

            {
                field: 'rateBy',
                title: this.tOptions('panel-rateby'),
                minWidth: 120,
                id: 'panel-rateby',
                filter: 'text',
                lovData: [this.tOptions('PERIOD'), this.tOptions('TIMEPOINTS')],
            },
            {
                field: 'scaleBy',
                title: this.tOptions('panel-scaleby'),
                minWidth: 170,
                id: 'panel-scaleby',
                filter: 'text',
                lovData: [
                    this.tOptions(ModelScaleBy.PER_SUBJECT_WEEK),
                    this.tOptions(ModelScaleBy.PER_SUBJECT_MONTH),
                    this.tOptions(ModelScaleBy.PER_SUBJECT_YEAR),
                    this.tOptions(ModelScaleBy.PER_10_SUBJECT_YEARS),
                    this.tOptions(ModelScaleBy.PER_100_SUBJECT_YEARS),
                    this.tOptions(ModelScaleBy.PER_1000_SUBJECT_YEARS),
                ],
            },
            {
                field: 'scaleTimepoint',
                title: this.tOptions('panel-scaleTimepoint'),
                minWidth: 170,
                id: 'panel-scaleTimepoint',
                filter: 'text',
                lovData: [
                    this.tOptions(ModelScaleBy.PER_SUBJECT_TIME_POINT),
                    this.tOptions(ModelScaleBy.PER_10_SUBJECT_TIME_POINTS),
                    this.tOptions(ModelScaleBy.PER_100_SUBJECT_TIME_POINTS),
                    this.tOptions(ModelScaleBy.PER_1000_SUBJECT_TIME_POINTS),
                ],
            },
            {
                field: 'timepointsList',
                title: this.tOptions('panel-timepointslist'),
                minWidth: 210,
                id: 'panel-timepointslist',
                filter: 'text',
                cells: { data: this.TooltipCell({ isCsv: false }) },
            },
            {
                field: 'timepointDate',
                title: this.tOptions('panel-timepointdate'),
                id: 'panel-timepointdate',
                filter: 'text',
                minWidth: 210,
                cells: { data: this.TooltipCell({ isCsv: false }) },
            },

            {
                field: 'plotBy',
                title: this.tOptions('panel-plotby'),
                minWidth: 140,
                id: 'panel-plotby',
                filter: 'text',
                lovData: [this.tOptions(ModelPlotBy.MONTHS), this.tOptions(ModelPlotBy.WEEKS)],
            },
            {
                field: 'cumulativeOrTimePeriod',
                title: this.tOptions('panel-plotcalculation'),
                minWidth: 140,
                id: 'panel-plotcalculation',
                filter: 'text',
                lovData: [
                    this.tOptions(ModelPlotCalculation.CUMULATIVE),
                    this.tOptions(ModelPlotCalculation.BY_TIME_PERIOD),
                ],
            },
            {
                title: this.tOptions('panel-automation'),
                id: 'panel-automation',
                field: 'field-automation',
                children: [
                    {
                        field: 'automationRange',
                        title: this.tOptions('panel-automationRange'),
                        id: 'panel-automationRange',
                        filter: 'text',
                        minWidth: 135,
                        lovData: [this.tOptions(AutomationRange.ALL), this.tOptions(AutomationRange.SPECIFIC_RANGE)],
                    },
                    {
                        field: 'numberOfTimePeriods',
                        title: this.tOptions('panel-numberOfTimePeriods'),
                        id: 'panel-numberOfTimePeriods',
                        minWidth: 100,
                        filter: 'numeric',
                    },
                ],
            },
            {
                title: this.tOptions('panel-xaxiscalculation'),
                id: 'panel-xaxiscalculation',
                field: 'field-xaxiscalc',
                children: [
                    {
                        field: 'calcStartDate',
                        title: this.tOptions('panel-xaxisstartdate'),
                        id: 'panel-xaxisstartdate',
                        filter: 'text',
                        minWidth: 210,
                        cells: { data: this.TooltipCell({ isCsv: false }) },
                    },
                    {
                        field: 'calcEndDates',
                        title: this.tOptions('panel-xaxisenddates'),
                        id: 'panel-xaxisenddates',
                        filter: 'text',
                        minWidth: 210,
                        cells: { data: this.TooltipCell({ isCsv: true }) },
                    },
                ],
            },
            {
                title: this.tOptions('panel-thresholds'),
                id: 'panel-thresholds',
                field: 'field-thresholds',
                children: [
                    {
                        field: 'thresholdType',
                        title: this.tOptions('panel-thresholdtype'),
                        minWidth: 170,
                        id: 'panel-thresholdtype',
                        filter: 'text',
                        lovData: [
                            this.tOptions(EventRatesOTThresholdType.FIXED),
                            this.tOptions(EventRatesOTThresholdType.LANEY_U_CHART),
                            this.tOptions(EventRatesOTThresholdType.U_CHART),
                            this.tOptions(EventRatesOTThresholdType.QUASI_POISSON_U_CHART),
                        ],
                    },
                    {
                        field: 'eventRatesOverTimeParameters.upperAmber',
                        title: this.tOptions('panel-upperAmberThreshold'),
                        minWidth: 100,
                        filter: 'numeric',
                        id: 'panel-upperAmberThreshold',
                    },
                    {
                        field: 'eventRatesOverTimeParameters.upperRed',
                        title: this.tOptions('panel-upperRedThreshold'),
                        minWidth: 100,
                        filter: 'numeric',
                        id: 'panel-upperRedThreshold',
                    },
                    {
                        field: 'eventRatesOverTimeParameters.lowerAmber',
                        title: this.tOptions('panel-lowerAmberThreshold'),
                        minWidth: 100,
                        filter: 'numeric',
                        id: 'panel-lowerAmberThreshold',
                    },
                    {
                        field: 'eventRatesOverTimeParameters.lowerRed',
                        title: this.tOptions('panel-lowerRedThreshold'),
                        minWidth: 100,
                        filter: 'numeric',
                        id: 'panel-lowerRedThreshold',
                    },
                ],
            },
            {
                title: this.tOptions('panel-benchmark'),
                id: 'panel-benchmark',
                field: 'field-benchmark',
                children: [
                    {
                        field: 'benchmarkType',
                        title: this.tOptions('panel-benchmarktype'),
                        minWidth: 140,
                        id: 'panel-benchmarktype',
                        filter: 'text',
                        lovData: [
                            this.tOptions(EventRatesOTBenchmarkType.NONE),
                            this.tOptions(EventRatesOTBenchmarkType.STUDY_SPECIFIC),
                            this.tOptions(EventRatesOTBenchmarkType.STUDY_RATE),
                        ],
                    },
                    {
                        field: 'eventRatesOverTimeParameters.studySpecificBenchmark',
                        title: this.tOptions('panel-benchmarkstudyspecificvalue'),
                        minWidth: 100,
                        filter: 'numeric',
                        id: 'panel-benchmarkstudyspecificvalue',
                    },
                    {
                        field: 'countryBenchmark',
                        title: this.tOptions('panel-countrybenchmark'),
                        minWidth: 140,
                        filter: 'text',
                        id: 'panel-countrybenchmark',
                        lovData: [
                            this.tOptions(EventRatesOTCountryBenchmarkType.COUNTRY_RATE),
                            this.tOptions(EventRatesOTCountryBenchmarkType.NONE),
                            this.tOptions(EventRatesOTCountryBenchmarkType.STUDY_SPECIFIC),
                            this.tOptions(EventRatesOTCountryBenchmarkType.STUDY_RATE),
                        ],
                    },
                ],
            },

            {
                title: this.tOptions('panel-mintimeonstudy'),
                id: 'panel-mintimeonstudy',
                field: 'field-timeonstudy',
                children: [
                    {
                        field: 'eventRatesOverTimeParameters.minSubjects',
                        title: this.tOptions('panel-minsubjects'),
                        id: 'panel-minsubjects',
                        minWidth: 70,
                        filter: 'numeric',
                    },
                    {
                        field: 'eventRatesOverTimeParameters.minTimeOnStudyUnit',
                        title: this.tOptions('panel-mintimeonstudyunit'),
                        id: 'panel-unit',
                        filter: 'numeric',
                        minWidth: 70,
                    },
                    {
                        field: 'minTimeOnStudyPeriod',
                        title: this.tOptions('panel-mintimeonstudyperiod'),
                        id: 'panel-period',
                        filter: 'text',
                        minWidth: 100,
                        lovData: [
                            this.tOptions(ControlHubModelPeriodType.DAYS),
                            this.tOptions(ControlHubModelPeriodType.WEEKS),
                            this.tOptions(ControlHubModelPeriodType.MONTHS),
                            this.tOptions(ControlHubModelPeriodType.QUARTERS),
                            this.tOptions(ControlHubModelPeriodType.YEARS),
                        ],
                    },
                ],
            },
            {
                field: 'eventRatesOverTimeParameters.expectedVolume',
                title: this.tOptions('panel-expectedvolume'),
                id: 'panel-expectedvolume',
                minWidth: 70,
                filter: 'numeric',
            },
            {
                field: 'eventRatesOverTimeParameters.xAxisPtsToShade',
                title: this.tOptions('panel-xaxisptstoshade'),
                id: 'panel-xaxisptstoshade',
                minWidth: 70,
                filter: 'numeric',
            },
            {
                field: 'eventRatesOverTimeParameters.numeratorName',
                title: this.tOptions('panel-NumeratorField'),
                id: 'panel-NumeratorField',
                filter: 'text',
                minWidth: 210,
                cells: { data: this.TooltipCell({ isCsv: false }) },
            },
            {
                field: 'eventRatesOverTimeParameters.denominatorName',
                title: this.tOptions('panel-DenominatorField'),
                id: 'panel-DenominatorField',
                filter: 'text',
                minWidth: 210,
                cells: { data: this.TooltipCell({ isCsv: false }) },
            },
            ...commonCreatedUpdatedColumns(this.t),
            ...commonHiddenColumns(this.t),
        ];
    }
}

export default EventRatesOTGrid;
